/**
 * Mirrors the `filters:` block of `winsight-server`'s `reports/t2-business/report.yaml`, which is
 * authoritative.
 *
 * **The schema is a function of the tab**, because the views bucket time differently and the server
 * declares that with `views:`. by-year, by-month and sdp bind a calendar `year` into `MAKEDATE` on
 * the partition key; by-season takes a `from`/`to` window instead, because a season crosses calendar
 * years. Showing all the controls at once would offer the reader two period pickers, only one of
 * which does anything.
 *
 * sdp is the sharper case: it reports delivery, not spend, so it takes neither `basis` nor the
 * season dimension its query does not read. Sending one is a 400 from the server, which is the
 * same rule stated once rather than twice. Company code is required on every tab, including sdp.
 */

import { format, startOfYear, subYears } from 'date-fns';

import { liveOptionsFor, type FilterOption, type FilterSchema } from '@/lib/filters';

/** Option values come from `report.yaml`'s `options:` block, never a hardcoded array (§5.4). */
const live = liveOptionsFor('t2-business');

const ISO = 'yyyy-MM-dd';

/**
 * The view's own floor. The fact view starts at 2023 — before that the FX rates the money columns
 * depend on do not exist, and the report would silently show a short year.
 */
export const FIRST_YEAR = 2023;

/** Years the picker offers, newest first. */
export function yearOptions(today: Date = new Date()): readonly FilterOption[] {
  const latest = today.getFullYear();
  const out: FilterOption[] = [];
  for (let y = latest; y >= FIRST_YEAR; y -= 1) {
    out.push({ value: String(y), label: String(y) });
  }
  return out;
}

/** Opens on the current year: the sheet's rightmost column is always the year in progress. */
export function defaultYear(today: Date = new Date()): string {
  return String(today.getFullYear());
}

/** Two years back to today — the same three-year span the by-year grid shows. */
export function defaultSeasonPeriod(today: Date = new Date()): { from: string; to: string } {
  return { from: format(startOfYear(subYears(today, 2)), ISO), to: format(today, ISO) };
}

export type BusinessTab = 'by-year' | 'by-month' | 'by-season' | 'sdp';

/**
 * A governed pair, not a list read from the data: these name which source columns a measure sums,
 * and the data has no column that would enumerate them.
 */
const BASIS_OPTIONS: readonly FilterOption[] = [
  { value: 'ordered', label: 'Ordered' },
  { value: 'invoiced', label: 'Invoiced' },
];

const INTERLINING_OPTIONS: readonly FilterOption[] = [
  { value: 'true', label: 'Included' },
  { value: 'false', label: 'Excluded' },
];

/**
 * PI is the effective delivery month: updated PI delivery date, falling back to confirmed PI
 * delivery date. LCO is the buy month parsed out of `lc0_date_remark`. Not the same question as
 * `basis`: this chooses which month a line is counted in, not which columns a measure reads.
 */
export const DATE_BASIS_OPTIONS: readonly FilterOption[] = [
  { value: 'PI', label: 'PI month' },
  { value: 'LCO', label: 'LCO month' },
];

export const DEFAULT_COMPANY_CODE = 'H018';

export function t2BusinessFilters(tab: BusinessTab): FilterSchema {
  const companyCode: FilterSchema = [
    {
      type: 'select',
      name: 'buyer',
      label: 'Company Code',
      required: true,
      options: live('buyer'),
      defaultValue: DEFAULT_COMPANY_CODE,
    },
  ];

  const year: FilterSchema = [
    {
      type: 'select',
      name: 'year',
      label: 'Year',
      required: true,
      options: yearOptions(),
      defaultValue: defaultYear,
    },
  ];

  const materialClass: FilterSchema = [
    // Fabric against trims. The users' spreadsheet splits every table on this, so it leads the
    // optional filters rather than sitting at the end. Named the way the grid names it (§4.10).
    {
      type: 'select',
      name: 'material_class',
      label: 'Material Type',
      anyLabel: 'Fabric & Acc.',
      options: live('material_class'),
    },
  ];

  const includeInterlining: FilterSchema = [
    {
      type: 'select',
      name: 'include_interlining',
      label: 'Include Interlining?',
      required: true,
      options: INTERLINING_OPTIONS,
      presentation: 'segmented',
      defaultValue: 'true',
    },
  ];

  const supplierGroup: FilterSchema = [
    {
      type: 'multi-select',
      name: 't2_group',
      label: 'Supplier group',
      anyLabel: 'All suppliers',
      options: live('t2_group'),
    },
  ];

  const factory: FilterSchema = [
    {
      type: 'multi-select',
      name: 'factory',
      label: 'Factory',
      anyLabel: 'All factories',
      options: live('factory'),
    },
  ];

  if (tab === 'sdp') {
    return [
      ...companyCode,
      ...year,
      {
        type: 'select',
        name: 'date_basis',
        label: 'Count by',
        required: true,
        options: DATE_BASIS_OPTIONS,
        defaultValue: 'PI',
      },
      ...materialClass,
      ...supplierGroup,
      ...factory,
      ...includeInterlining,
    ];
  }

  const period: FilterSchema =
    tab === 'by-season'
      ? [
          {
            type: 'date-range',
            name: 'period',
            label: 'Order date',
            required: true,
            fromParam: 'from',
            toParam: 'to',
            defaultValue: defaultSeasonPeriod,
          },
        ]
      : year;

  return [
    ...companyCode,
    ...period,
    // Ordered against invoiced. Every measure on the page reads one or the other, so it leads the
    // bar and is never empty — there is no "both" to fall back to (§5.4).
    {
      type: 'select',
      name: 'basis',
      label: 'Basis',
      required: true,
      options: BASIS_OPTIONS,
      defaultValue: 'ordered',
    },
    ...materialClass,
    // Normalised on the server: picking SS26 also brings in the S26 and SP26 spellings of the same
    // buy, and the list offers only the canonical SS/FW codes (report.yaml).
    {
      type: 'multi-select',
      name: 'season',
      label: 'Season',
      anyLabel: 'All seasons',
      options: live('season'),
    },
    ...supplierGroup,
    ...factory,
    ...includeInterlining,
  ];
}
